import React, { useState, useEffect } from 'react';
import { Form, Button, Input, Select, Modal, Spin, Card, Table, InputNumber } from 'antd';
import { Plus, ArrowLeft } from 'lucide-react';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { useLocation, useNavigate } from 'react-router-dom';
import AxiosCaller from '../../../../authenticatie/AxiosCaller';
import './MakenAanpassenMenu.css';
import { openNotificationSuccess, openNotificationError } from '../../../../Components/Notification/Notification';


const { Option } = Select;

const MakenAanpassenMenu = () => {
  const { get, put, post } = AxiosCaller();
  const navigate = useNavigate();
  const location = useLocation();
  const menu = location.state;
  
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(true);
  const [ingredienten, setIngredienten] = useState([]);
  const [dishTypes, setDishTypes] = useState([]);
  const [klasTypes, setKlasTypes] = useState([]);
  const [newProductRows, setNewProductRows] = useState([{ product: '', aantal: 1 }]);
       const [maxHeight, setMaxHeight] = useState(0);
        
            useEffect(() => {
                const updateHeight = () => {
                    setMaxHeight(0.7 * document.documentElement.clientHeight);
                };
        
                updateHeight(); // Initialiseren bij eerste render
                window.addEventListener("resize", updateHeight); // Updaten bij schermresizing
        
                return () => window.removeEventListener("resize", updateHeight); // Cleanup
            }, []);

  // Fetching data for ingredients, dish types, and class types
  useEffect(() => {
    const fetchData = async () => {
      try {
        await Promise.all([
          fetchIngredienten(),
          fetchDishTypes(),
          fetchKlasTypes()
        ]);

        setLoading(false);

        if (menu) {
          updateFields(menu);
        }
      } catch (error) {
        console.error('Error fetching data', error);
        setLoading(false);
       openNotificationError("Fout bij ophalen van de data" , error)
      }
    };

    fetchData();
  }, [menu]);

  const fetchIngredienten = async () => {
    try{
      const response = await get('/product');
      setIngredienten(response.data);
    }catch(error){
      console.error(error)
      openNotificationError("Fout bij ophalen van producten", error)
    }
    
  };

  const fetchDishTypes = () => {
    const types = [
      { _id: 'voorgerecht', naam: 'Voorgerecht' },
      { _id: 'hoofdgerecht', naam: 'Hoofdgerecht' },
      { _id: 'nagerecht', naam: 'Nagerecht' }
    ];
    setDishTypes(types);
  };

  const fetchKlasTypes = async () => {
    try{
      const response = await get('/klasType');
    setKlasTypes(response.data);
    }catch(error){
      console.error(error)
      openNotificationError("Fout bij ophalen van klastypes", error)
    }
  
  };

  const updateFields = (menu) => {
    form.setFieldsValue({
      menuName: menu.naam,
      dishType: menu.type,
      klasType: menu.klasType?._id,
      aantalPersonen: menu.aantalPersonen,
    });

    const existingProducts = menu.producten.map((product) => ({
      product: product.product,
      aantal: product.aantal * menu.aantalPersonen,
    }));
    setNewProductRows(existingProducts);
  };

  const handleSaveMenu = async () => {
    try {
      // Validate form and ensure at least one product is added
      const values = await form.validateFields();

      if (newProductRows.length === 0 || newProductRows.every(row => !row.product)) {
        openNotificationError("Voeg minstens 1 product toe")
        return;
      }

      const productsArray = newProductRows
        .filter(row => row.product)
        .map((row) => ({
          product: row.product._id || row.product,
          aantal: row.aantal,
        }));

      const formData = {
        naam: values.menuName,
        type: values.dishType,
        aantalPersonen: values.aantalPersonen,
        klasType: values.klasType,
        producten: productsArray,
      };

      try {
        if (menu) {
          await put(`/recept/${menu._id}`, formData);
        } else {
          await post('/recept', formData);
        }

        form.resetFields();
        setNewProductRows([{ product: '', aantal: 1 }]);
        navigate('/Admin/configuratie/OverzichtMenu');
      } catch (error) {
        console.error('Error saving menu data:', error);
        openNotificationError("Fout bij opslaan menu", error)
      }
    } catch (error) {
      console.error('Validation error:', error);
      openNotificationError("Validatie fout", error)
    }
  };

  const handleAddProductRow = () => {
    setNewProductRows([...newProductRows, { product: '', aantal: 1 }]);
  };

  const handleProductChange = (value, index) => {
    const selectedProduct = ingredienten.find((product) => product._id === value);
    if (!selectedProduct) return;
  
    setNewProductRows((prevRows) => {
      let updatedRows = [...prevRows];
  
      // Check if the new product already exists somewhere else
      const existingIndex = updatedRows.findIndex((row, i) => row.product?._id === value && i !== index);
  
      if (existingIndex !== -1) {
        // If product already exists, sum up the "aantal" and remove the old row
        updatedRows[existingIndex].aantal = String(
          parseInt(updatedRows[existingIndex].aantal || "0", 10) +
          parseInt(updatedRows[index]?.aantal || "1", 10)
        );
        updatedRows.splice(index, 1); // Remove the duplicate row
      } else {
        // Otherwise, simply update the product for the current row
        updatedRows[index] = { ...updatedRows[index], product: selectedProduct };
      }
  
      return updatedRows;
    });
  };
  
  const handleAantalChange = (value, index) => {
    setNewProductRows((prevRows) => {
      const updatedRows = [...prevRows];
      updatedRows[index].aantal = String(value); // Ensure it remains a string
      return updatedRows;
    });
  };

  const terug = () => {
    navigate('/Admin/configuratie/OverzichtMenu');
  };

  if (loading) {
    return <Spin size="large" style={{ marginTop: '20%', display: 'block', marginLeft: 'auto', marginRight: 'auto' }} />;
  }

  return (
    <div className="Screen-container">
      <div className="title-container">
        <div className="flex gap">
          <ArrowLeftOutlined className="back-arrow" onClick={terug} />
          <h1 className="title">Menu</h1>
        </div>
      </div>

      <Form 
        form={form} 
        layout="vertical" 
        onFinish={handleSaveMenu}
        className='form'
        style={{ overflowY: "auto", maxHeight }}
      >
        <Form.Item 
          label="Naam" 
          name="menuName"
          rules={[{ required: true, message: 'Naam is verplicht' }]}
        >
          <Input placeholder="Voer naam in" />
        </Form.Item>

        <Form.Item 
          label="Aantal Personen" 
          name="aantalPersonen"
          rules={[{ required: true, message: 'Aantal Personen is verplicht' }]}
        >
          <Input type="number" placeholder="Aantal personen" />
        </Form.Item>

        <Form.Item 
          label="Gerecht Type" 
          name="dishType"
          rules={[{ required: true, message: 'Gerecht Type is verplicht' }]}
        >
          <Select placeholder="Selecteer gerecht type">
            {dishTypes.map((type) => (
              <Option key={type._id} value={type._id}>
                {type.naam}
              </Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item 
          label="Klas Type" 
          name="klasType"
          rules={[{ required: true, message: 'Klas Type is verplicht' }]}
        >
          <Select placeholder="Selecteer klas type">
            {klasTypes.map((type) => (
              <Option key={type._id} value={type._id}>
                {type.naam}
              </Option>
            ))}
          </Select>
        </Form.Item>

        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '10px', marginTop: '20px' }}>
          <div style={{ display: 'flex', justifyContent: 'flex-start', width: '100%' }}>
            <Button type="primary" icon={<Plus />} onClick={handleAddProductRow}>
              Voeg product toe
            </Button>
          </div>

          <div style={{ width: '100%' }}>
            <h2 className="title">Overzicht Producten</h2>
            <Table
              columns={[
                { 
                  title: 'Producten', 
                  dataIndex: 'Producten', 
                  key: 'Producten',
                  render: (text, record, index) => (
                    <Select
                      showSearch
                      value={record.product?._id}
                      onChange={(value) => handleProductChange(value, index)}
                      placeholder="Selecteer product"
                      style={{ width: '100%' }}
                      optionFilterProp="children"
                      filterOption={(input, option) => {
                        const productName = option.children.toString().toLowerCase();
                        return productName.includes(input.toLowerCase());
                      }}
                    >
                      {ingredienten.map((product) => (
                        <Option key={product._id} value={product._id}>
                          {product.naam}
                          {product.eenheid && ` (1 ${product.eenheid.naam})`}
                        </Option>
                      ))}
                    </Select>
                  )
                },
                { 
                  title: 'Aantal', 
                  dataIndex: 'Aantal', 
                  key: 'Aantal',
                  render: (text, record, index) => (
                    <InputNumber
                      value={record.aantal}
                      onChange={(value) => handleAantalChange(value, index)}
                      min={1}
                      placeholder="Aantal"
                      style={{ width: '100%' }}
                    />
                  )
                },
                { 
                  title: 'Acties', 
                  dataIndex: 'Acties', 
                  key: 'Acties',
                  render: (text, record, index) => (
                    <Button 
                      onClick={() => setNewProductRows(newProductRows.filter((_, i) => i !== index))} 
                      danger
                    >
                      Verwijderen
                    </Button>
                  )
                },
              ]}
              dataSource={newProductRows.map((row, index) => ({
                key: index,
                ...row
              }))}
              pagination={false}
              scroll={{ y: 0.5 * window.innerHeight }}
            />
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-start', width: '100%', marginTop: '20px' }}>
            <Button type="primary" htmlType="submit">
              {menu ? 'Bewerk menu' : 'Voeg menu toe'}
            </Button>
          </div>
        </div>
      </Form>
    </div>
  );
};

export default MakenAanpassenMenu;